/**
 * `ai-consent` — the ONE explicit consent gate shown before the first AI
 * action in each flow (App Store Guideline 5.1.2(i) / GDPR Art. 6(1)(a)).
 *
 * MODELLED ON `useVoiceConsent` (`@/components/interview/VoiceConsentSheet`):
 * acceptance is recorded SERVER-SIDE as one `consent_records` row per type
 * (legal_basis 'consent'), which is what `requireConsent()` on the edge side
 * now gates on for `ai-score`, `rescore-submission`, `investor-analyze-deck`,
 * `pitch-deck-extract`, `startup-insights`, `strategic-analysis` and
 * `extract-team-profiles` (D2). The server row is the single source of truth
 * — there is no local cache to reset on sign-out, and consent follows the
 * user across devices.
 *
 * `useAiConsentGate` is the thing call sites actually wire up: it owns the
 * sheet's visibility, shows it when consent is missing, and — same
 * fail-safe direction as voice — NEVER calls the gated action on a failed
 * write. It also exposes `openConsentSheet()` for the 403-recovery path: a
 * call site that gets `'Consent required'` back from the server (a race, or
 * consent withdrawn from another device) reopens the sheet directly, without
 * trusting the (possibly stale) locally-cached `consented` flag.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useAuth } from '@/lib/supabase/auth';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { AiConsentSheet } from '@/components/consent/AiConsentSheet';

/**
 * The consent types D3 prompts for in-app. `voice` already has its own real
 * prompt (`VoiceConsentSheet`) and stays out of this gate; `document_indexing`,
 * `dd_chat` and `submission_processing` are not prompted in this workstream
 * (R-D-TYPES — only text the user actually sees gets a new version).
 */
export type AiConsentType =
  | 'ai_scoring'
  | 'pitch_deck_analysis'
  | 'startup_insights'
  | 'strategic_analysis'
  | 'team_extraction';

/**
 * Keep in sync with `CONSENT_VERSIONS` in
 * `supabase/functions/_shared/consent/types.ts` (audit-trail consistency).
 * Per R-D-TYPES: `ai_scoring` / `pitch_deck_analysis` / `startup_insights` /
 * `strategic_analysis` moved to '1.1' (the in-app sheet names the AI
 * providers for the first time, and `strategic_analysis` had no in-app
 * prompt at all before this). `team_extraction` stays at '1.0' — its purpose
 * text did not change.
 */
export const AI_CONSENT_VERSIONS: Record<AiConsentType, string> = {
  ai_scoring: '1.1',
  pitch_deck_analysis: '1.1',
  startup_insights: '1.1',
  strategic_analysis: '1.1',
  team_extraction: '1.0',
};

/**
 * Keep in sync with `CONSENT_PURPOSES` in
 * `supabase/functions/_shared/consent/types.ts` — copied verbatim so the
 * sheet's copy and the server's audit-trail `purpose` column never drift.
 */
export const AI_CONSENT_PURPOSES: Record<AiConsentType, string> = {
  ai_scoring:
    'AI-powered analysis of your startup across 15 investment dimensions using large language models',
  pitch_deck_analysis:
    'AI analysis of pitch deck content, structure, and narrative for investment evaluation',
  startup_insights:
    'AI-generated insights and recommendations derived from your startup data and market analysis',
  strategic_analysis:
    'AI and external-source research used to produce evidence-linked competitive, market, industry-force, PESTEL, and exit-strategy analyses for a startup.',
  team_extraction:
    'Automated extraction and structuring of team member information from submitted documents using AI',
};

interface ConsentRow {
  consent_type: AiConsentType;
  consent_version: string;
}

/**
 * Shared shape of a granted `consent_records` row — reused by every
 * consent-granting hook in the app so the field list (and the
 * `legal_basis: 'consent'` / `granted_at` defaults) is defined exactly once.
 * `VoiceConsentSheet`'s `useVoiceConsent` uses this for its single-type
 * insert; `useAiConsent` below maps it over every missing type.
 */
export function buildConsentGrantRow(input: {
  userId: string;
  consentType: string;
  version: string;
  purpose: string;
}) {
  return {
    user_id: input.userId,
    consent_type: input.consentType,
    granted: true,
    consent_version: input.version,
    purpose: input.purpose,
    legal_basis: 'consent' as const,
    granted_at: new Date().toISOString(),
  };
}

function isSatisfied(type: AiConsentType, rows: ConsentRow[] | undefined): boolean {
  if (!rows) return false;
  return rows.some((r) => r.consent_type === type && r.consent_version === AI_CONSENT_VERSIONS[type]);
}

/** A stable, order-independent query key for a given set of types. */
function typesKeyOf(types: AiConsentType[]): string {
  return [...types].sort().join(',');
}

/**
 * The read itself, split out of `useQuery` so the RECOVERY path (below) can
 * call it directly through `queryClient.fetchQuery` instead of trusting
 * whatever the mounted query's cache currently believes.
 */
async function fetchConsentRows(userId: string, types: AiConsentType[]): Promise<ConsentRow[]> {
  if (mode.isMock || !isSupabaseConfigured()) return [];
  const { data: rows, error } = await supabase
    .from('consent_records')
    .select('consent_type, consent_version')
    .eq('user_id', userId)
    .in('consent_type', types)
    .eq('granted', true)
    .is('withdrawn_at', null);
  if (error) {
    // Fail toward re-showing the gate; never toward silent consent.
    //
    // TOLERATED BY DESIGN, not a bug to "fix" into a hard failure: a failed
    // read here reads as "not consented", so `accept()` treats every
    // requested type as missing and inserts a fresh row — even if the user
    // was already consented and this read just failed to see it. That
    // duplicate-insert risk is accepted because withdrawal (see the consent
    // manager) withdraws every ACTIVE row of a given type, not just the
    // newest one, so a stray extra granted row is harmless: nothing reads
    // "most recent row" as authoritative, and withdrawal still clears the
    // user out completely.
    reportError(error, { context: 'useAiConsent.query', level: 'warning', silent: true });
    return [];
  }
  return (rows ?? []) as ConsentRow[];
}

export interface AcceptOptions {
  /**
   * Re-read the user's consent rows from the server BEFORE computing what is
   * missing, instead of trusting the mounted query's cache.
   *
   * Required for the 403-recovery path: the cache said "consented" (that is
   * WHY the gated action was allowed to fire), the server disagreed, and
   * `missing` computed against that same stale cache would stay empty —
   * `accept()` would insert nothing, `consented` would stay (wrongly) true,
   * and the retried action would 403 again. Every `fresh: true` accept is
   * asking "what does the server actually have right now", which is exactly
   * the question a recovery needs answered.
   */
  fresh?: boolean;
}

/**
 * Thrown by a `fresh` (recovery) accept when the server just refused, yet a
 * fresh read says every requested type is ALREADY satisfied. That can only
 * mean a version this gate's `AI_CONSENT_VERSIONS` doesn't know about, or a
 * type the server now requires that this request doesn't cover — blindly
 * treating it as a completed accept and retrying the gated action would
 * almost certainly 403 again with nothing new to insert next time either:
 * exactly the "tap I agree -> 403 -> tap again" loop a silent success here
 * would create. Surfaced to the sheet as an error instead.
 */
export class ConsentVerificationError extends Error {
  constructor() {
    super("We couldn't confirm your consent with the server. Please try again in a moment.");
    this.name = 'ConsentVerificationError';
  }
}

export interface UseAiConsent {
  /** True once every requested type has a granted, current-version row. False while loading (safe direction — re-shows the gate). */
  consented: boolean;
  /** Still reading the stored rows. */
  isLoading: boolean;
  /** Whether an `accept()` write is in flight. */
  isAccepting: boolean;
  /** Persist acceptance for every currently-missing type. Rejects with `ConsentVerificationError` on the drift case described there. */
  accept: (options?: AcceptOptions) => Promise<void>;
}

/**
 * Per-user consent for a SET of AI-processing types, backed by the server
 * `consent_records` table. Mirrors `useVoiceConsent`'s shape and fail-safe
 * direction; generalised to many types and to per-type version checks
 * (a stale-version row does NOT count — `verifyConsent` on the server applies
 * the same rule).
 */
export function useAiConsent(types: AiConsentType[]): UseAiConsent {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const queryKey = ['ai-consent', userId, typesKeyOf(types)] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!userId && types.length > 0,
    staleTime: Infinity,
    queryFn: () => (userId ? fetchConsentRows(userId, types) : Promise.resolve([])),
  });

  const mutation = useMutation({
    mutationFn: async (options?: AcceptOptions) => {
      if (!userId) throw new Error('Cannot record AI consent without a signed-in user');
      if (mode.isMock || !isSupabaseConfigured()) return; // demo/mock: no DB, no real AI call path
      // `staleTime: Infinity` on the mounted query means `data` can be an
      // arbitrarily old snapshot — force a real read when the caller says so
      // (see `AcceptOptions.fresh`), rather than the cache this write is
      // trying to CORRECT.
      const knownRows = options?.fresh
        ? await queryClient.fetchQuery({ queryKey, queryFn: () => fetchConsentRows(userId, types), staleTime: 0 })
        : data;
      const missing = types.filter((t) => !isSatisfied(t, knownRows));
      if (missing.length === 0) {
        if (options?.fresh) {
          // See ConsentVerificationError: a recovery accept found nothing to
          // insert, which does not explain the 403 that triggered it.
          throw new ConsentVerificationError();
        }
        return;
      }
      const rows = missing.map((t) =>
        buildConsentGrantRow({
          userId,
          consentType: t,
          version: AI_CONSENT_VERSIONS[t],
          purpose: AI_CONSENT_PURPOSES[t],
        }),
      );
      const { error } = await supabase.from('consent_records').insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.setQueryData<ConsentRow[]>(
        queryKey,
        types.map((t) => ({ consent_type: t, consent_version: AI_CONSENT_VERSIONS[t] })),
      );
    },
    onError: (err) => {
      // BLOCKING: every gated function now 403s without the server row, so a
      // failed write must NOT proceed as if consented. The caller keeps the
      // sheet open; the user can retry.
      reportError(err, { context: 'useAiConsent.accept', level: 'warning' });
    },
  });

  const consented = types.length > 0 && types.every((t) => isSatisfied(t, data));

  return {
    consented,
    isLoading: !!userId && isLoading,
    isAccepting: mutation.isPending,
    accept: async (options?: AcceptOptions) => {
      await mutation.mutateAsync(options);
    },
  };
}

export interface UseAiConsentGate {
  /**
   * Runs `action` immediately if already consented; otherwise shows the sheet
   * and runs it right after a successful accept. While the consent read is
   * still in flight, the decision is DEFERRED until it resolves rather than
   * showing the sheet on a `consented === false` that only means "not known
   * yet" — the query starts `false` on every mount, so deciding early would
   * both flash a redundant sheet for an already-consented user and let a fast
   * double-press queue two inserts of the same row.
   */
  runWithConsent: (action: () => void) => void;
  /**
   * Opens the sheet directly, bypassing the locally-cached `consented` flag —
   * for recovering from a server 403 (`'Consent required'`) that the cache
   * did not predict (a race, or consent withdrawn elsewhere). Pass `action`
   * to re-run the original call automatically once consent is re-granted;
   * omit it to just let the person re-consent and retry manually. The
   * follow-up `accept()` this triggers always re-reads the server first
   * (`AcceptOptions.fresh`) rather than trusting the cache that was just
   * proven wrong.
   */
  openConsentSheet: (action?: () => void) => void;
  /** Whether the locally-cached state currently considers every requested type consented. */
  consented: boolean;
  /** Still reading the stored rows — see `runWithConsent`'s deferral. */
  isLoading: boolean;
  /** Render this wherever the gate is used — it is the sheet itself (hidden until needed). */
  sheet: React.ReactElement;
}

export interface UseAiConsentGateOptions {
  /**
   * Runs when the person declines. The default gate contract is "declining
   * simply doesn't run the action" (the caller's own screen stays put) — this
   * is only for the rare flow that must CONTINUE regardless of the decision
   * (e.g. onboarding completion still navigates to the dashboard whether or
   * not the founder consents to being scored on the way there).
   */
  onDecline?: () => void;
}

/**
 * The gate every AI-action call site wires up:
 * `const { runWithConsent, sheet } = useAiConsentGate(['ai_scoring']);`
 * `render(sheet)` and wrap the triggering handler:
 * `onPress={() => runWithConsent(startScoring)}`.
 */
export function useAiConsentGate(
  types: AiConsentType[],
  options: UseAiConsentGateOptions = {},
): UseAiConsentGate {
  const { consented, isLoading, isAccepting, accept } = useAiConsent(types);
  const [visible, setVisible] = useState(false);
  const router = useRouter();
  // Refs, not state: none of them drive a render on their own, and none must
  // survive past the decision that consumes it (accept or decline).
  const pendingActionRef = useRef<(() => void) | null>(null);
  const onDeclineRef = useRef(options.onDecline);
  onDeclineRef.current = options.onDecline;
  // Whether the CURRENTLY open sheet was opened via `openConsentSheet`
  // (server said no) rather than `runWithConsent` (cache said no) — decides
  // whether the follow-up accept must re-read the server first.
  const isRecoveryRef = useRef(false);
  // `consented` as of the LATEST render, read from inside a callback that a
  // stale closure would otherwise evaluate against the render that created
  // it (see `decide`, called from an effect one render after the query
  // resolves).
  const consentedRef = useRef(consented);
  consentedRef.current = consented;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  // An action that arrived while the consent read was still in flight —
  // resolved by the effect below once it settles, never decided on a
  // `consented === false` that only means "not known yet".
  const deferredRef = useRef<{ action: (() => void) | null; recovery: boolean } | null>(null);
  // A ConsentVerificationError message from the last accept attempt — shown
  // inline in the sheet. Local state (not a ref): the sheet needs to
  // re-render to show it. Reset on every close and at the start of every
  // new attempt, same as `pendingActionRef` — the AppModal-never-unmounts
  // rule applies to this too.
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    setVisible(false);
    pendingActionRef.current = null;
    isRecoveryRef.current = false;
    setError(null);
  }, []);

  const decline = useCallback(() => {
    close();
    onDeclineRef.current?.();
  }, [close]);

  /** The one place that actually decides: call the action, or show the sheet. */
  const decide = useCallback((action: (() => void) | null, recovery: boolean) => {
    if (!recovery && consentedRef.current) {
      action?.();
      return;
    }
    isRecoveryRef.current = recovery;
    pendingActionRef.current = action;
    setVisible(true);
  }, []);

  const runWithConsent = useCallback(
    (action: () => void) => {
      if (isLoadingRef.current) {
        deferredRef.current = { action, recovery: false };
        return;
      }
      decide(action, false);
    },
    [decide],
  );

  const openConsentSheet = useCallback(
    (action?: () => void) => {
      if (isLoadingRef.current) {
        deferredRef.current = { action: action ?? null, recovery: true };
        return;
      }
      decide(action ?? null, true);
    },
    [decide],
  );

  // Resolves a call that arrived mid-load, exactly once, the moment the read
  // settles — never on every render while it is pending.
  useEffect(() => {
    if (!isLoading && deferredRef.current) {
      const { action, recovery } = deferredRef.current;
      deferredRef.current = null;
      decide(action, recovery);
    }
  }, [isLoading, decide]);

  const handleAccept = useCallback(async () => {
    setError(null);
    try {
      // A recovery accept re-reads the server first — the cache is exactly
      // what was just proven wrong.
      await accept(isRecoveryRef.current ? { fresh: true } : undefined);
    } catch (err) {
      // A drift the accept itself detected (ConsentVerificationError) gets a
      // message in the sheet; any other failure is already reported inside
      // useAiConsent. Either way the sheet stays open so the person can
      // retry rather than silently proceeding unconsented.
      if (err instanceof ConsentVerificationError) {
        setError(err.message);
      }
      return;
    }
    setVisible(false);
    const pending = pendingActionRef.current;
    pendingActionRef.current = null;
    isRecoveryRef.current = false;
    pending?.();
  }, [accept]);

  // Tapping the disclosure link inside an OPEN native Modal navigates
  // underneath it — the page renders, but the modal is still on top and
  // nothing is visible. `decline()` (not just `close()`) so this behaves
  // exactly as if "Not now" had been tapped — dropping the pending action
  // AND firing `onDecline`, which is what a caller like onboarding-complete
  // (whose "Go to Dashboard" button stays disabled until `onDecline` fires)
  // or data-room-connect (whose connection is only persisted via
  // `onDecline`) depends on — before navigating once the modal is gone.
  const handleOpenDisclosure = useCallback(() => {
    decline();
    router.push('/ai-disclosure');
  }, [decline, router]);

  const sheet = React.createElement(AiConsentSheet, {
    visible,
    types,
    isAccepting,
    errorMessage: error,
    onAccept: handleAccept,
    onDecline: decline,
    onOpenDisclosure: handleOpenDisclosure,
  });

  return { runWithConsent, openConsentSheet, consented, isLoading, sheet };
}
